import { yupResolver } from "@hookform/resolvers/yup";
import { useDisclosure } from "@nextui-org/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { deleteFetcher, getFetcher, postFetcher } from "../../services/api";
import { isResponseErrorObject } from "../../services/helpers";
import useSWR from "swr";
import useListCompany from "../useListCompany";
import { useSelectedCompany } from "../useSelectedCompany";
import { asalMuatValidation } from "../../services/validation/asal-muat.validation";

export default function useAsalMuat() {
    const {isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm} = useDisclosure();
    // Filter tabel: otomatis mengikuti perusahaan milik pengguna untuk
    // non-super-admin, null untuk super admin supaya bisa memilih sendiri.
    const [selectedCompany, setSelectedCompany] = useSelectedCompany();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [editId, setEditId] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const { register, handleSubmit, reset, control, watch, setValue, formState: { errors, isSubmitting, dirtyFields } } = useForm({resolver: yupResolver(asalMuatValidation)});
    const provinceId = watch('provinceId')
    const regencyId = watch('regencyId')
    const districtId = watch('districtId')
    const { dataCompany, isLoadingCompany } = useListCompany()
    // Perusahaan milik pengguna yang sedang login; null bila super admin.
    const [perusahaanLogin] = useSelectedCompany();

    /*
     * Daftar pilihan untuk kolom "Perusahaan Asal Muat".
     *
     * Bukan seluruh perusahaan: hanya yang sudah tertaut SIMPEL
     * (idCompanySimpel terisi) DAN bertipe Impor, Produksi, atau Distribusi B3 -
     * sesuai ketentuan bahwa asal muat hanya boleh berasal dari perusahaan
     * terdaftar dengan tiga tipe tersebut.
     *
     * Penyaringan dikerjakan di backend lewat search-company, bukan di browser,
     * supaya 8.585 baris perusahaan tidak perlu diunduh semuanya.
     */
    const TIPE_ASAL_MUAT = ['Impor B3', 'Produksi B3', 'Distribusi B3'].join(',')
    const { data: dataPerusahaanAsalMuat, isLoading: isLoadingPerusahaanAsalMuat } = useSWR(
        `/api/company/search-company?returnAll=true&hasIdCompanySimpel=true&tipePerusahaan=${encodeURIComponent(TIPE_ASAL_MUAT)}`,
        getFetcher
    )

    // Nama perusahaan yang disimpan adalah teks, dan beberapa perusahaan memakai
    // nama yang sama persis. Didedup supaya tidak ada dua opsi kembar yang
    // tidak bisa dibedakan pengguna.
    const opsiPerusahaanAsalMuat = [
        ...new Map(
            (dataPerusahaanAsalMuat?.data ?? [])
                .filter((item) => item?.name)
                .map((item) => [item.name.trim(), { label: item.name.trim(), value: item.name.trim(), perusahaan: item }])
        ).values(),
    ].sort((a, b) => a.label.localeCompare(b.label, 'id'))
    const { data, isLoading, mutate } = useSWR(selectedCompany ? `/api/company/search-perusahaan-asal-muat?page=${page + 1}&limit=${pageSize}&companyId=${selectedCompany.value}` : null, getFetcher);
    const { data: dataProvince } = useSWR('/api/location/provinces', getFetcher)
    const { data: dataRegency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}`: null, getFetcher)
    const { data: dataDistrict } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}`: null, getFetcher)
    const { data: dataVillage } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}`: null, getFetcher)

    function onClickEdit(item) {    
        setEditId(item.id);
        setIsEdit(true);
        reset({
            companyId: item.companyId,
            namaPerusahaan: item.namaPerusahaan,
            alamat: item.alamat,   
            locationType: item.locationType,   
            latitude: item.latitude,   
            longitude: item.longitude,   
            provinceId: item.provinceId,  
            regencyId: item.regencyId,
            districtId: item.districtId,
            villageId: item.villageId  
        });
        onOpenChangeModalForm();
    }

    /*
     * Buka form TAMBAH dengan perusahaan sudah terisi.
     *
     * Tombol Tambah sebelumnya memanggil onOpen useDisclosure langsung, jadi
     * form terbuka apa adanya dan pengguna harus memilih perusahaannya sendiri
     * padahal pilihannya sudah pasti. Untuk super admin tetap dikosongkan -
     * mereka memang harus memilih.
     */
    function onOpenFormTambah() {
        setEditId(null);
        setIsEdit(false);
        reset({
            companyId: perusahaanLogin?.value ?? '',
            namaPerusahaan: '',
            alamat: '',
            locationType: '',
            latitude: '',
            longitude: '',
            provinceId: '',
            regencyId: '',
            districtId: '',
            villageId: '',
        });
        onOpenModalForm();
    }

    function onCloseForm() {
        setEditId(null);
        setIsEdit(false);
        reset({
            companyId: '',
            namaPerusahaan: '',
            alamat: '',
            locationType: '',
            latitude: '',
            longitude: '',
            provinceId: '',
            regencyId: '',
            districtId: '',
            villageId: '',
        });
        onCloseModalForm()
    }
    
    async function onSubmitDelete(id) {
        try {
            await deleteFetcher('/api/company/asal-muat', id);
            mutate()
            toast.success('Asal muat berhasil dihapus!');
        } catch (error) {
            toast.error('Gagal hapus asal muat!');
        }
    }

    async function onSubmitForm(formData) {
        try {
            if (isEdit) {
                await postFetcher(`/api/company/asal-muat/${editId}`, formData);
                mutate()
                toast.success('Asal muat berhasil diubah!');
            } else {
                await postFetcher('/api/company/asal-muat', formData);
                const company = dataCompany?.data?.find((item) => item.id === formData.companyId)
                setSelectedCompany({label: company.name, value: company.id})
                mutate()
                toast.success('Asal muat berhasil ditambah!');
            }
            onCloseForm();
        } catch (error) {
            console.log(error);
            
            // isResponseErrorObject(error.response.data.message)
            //     ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            //         toast.error(value);
            //     })
            //     : toast.error(error.response.data.message)
        }
    }

    return {
        modalForm: {
            isOpenModalForm,
            // Sengaja bukan onOpen bawaan useDisclosure - lihat onOpenFormTambah.
            onOpenModalForm: onOpenFormTambah,
            onOpenChangeModalForm,
        },
        hookForm: {
            handleSubmit, 
            register,
            reset, 
            control,
            setValue,
            formState: { errors, isSubmitting, dirtyFields }
        },
        fetchData: {
            data,
            isLoading,
            dataCompany,
            isLoadingCompany,
            dataProvince,
            dataRegency,
            dataDistrict,
            dataVillage,
            opsiPerusahaanAsalMuat,
            isLoadingPerusahaanAsalMuat
        },
        state: {
            selectedCompany,
            setSelectedCompany,
            page,
            setPage,
            pageSize,
            setPageSize,
        },
        isEdit,
        onClickEdit,
        onCloseForm,
        onSubmitDelete,
        onSubmitForm,
        dataVillage,
    }
}